/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, Input } from 'antd';
import { Mail, Lock } from 'lucide-react';

import { toastError, toastSuccess } from '../../service/Toast';
import { getLoginErrorMessage } from './loginErrors';
import { setAdminProfile, setRole, setToken } from '../../service/CookieManager';
import { updateTokensAction } from '../actions';
import callApi from '../../Api/config';
import {
  AppRoutes,
  POST_LOGIN_DESTINATION_KEY,
  POST_LOGIN_WELCOME_PENDING_KEY,
  Role,
} from '../../constants/AppConstant';
import { ORDER_USER, PHOTO_ORDER_APPLICATION, USERS_ENTITY } from '../../constants/EntityName';
import { InvertFillButton } from '../../components/InvertFillButton';

import './styles.scss';

const LOGIN_URL = '/admin/login';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const onFinish = async (incomingPayload: { email: string }) => {
    setLoading(true);
    // Emails are case-insensitive: phones often capitalise the first letter.
    const payload = {
      ...incomingPayload,
      email: (incomingPayload.email || '').trim().toLowerCase(),
    };

    const response: any = await callApi(LOGIN_URL, 'POST', payload);

    const loginPayload = response?.data?.data;
    if (response?.data?.isSuccess && loginPayload) {
      const role = loginPayload.userRole || 'Super Admin';
      setRole(role);
      setAdminProfile({
        name: loginPayload.name ?? loginPayload.fullName,
        employeeId: loginPayload.employeeId ?? loginPayload.employee_id ?? loginPayload.employeeID,
        role,
        email: loginPayload.email ?? (incomingPayload as any).email,
      });
      localStorage.setItem('bucket', loginPayload.bucket);
      const token = loginPayload.accessToken;
      setToken(token);
      dispatch(updateTokensAction(token));
      let postLoginDestination = USERS_ENTITY;
      if (role === Role.ACCOUNTANT) {
        postLoginDestination = PHOTO_ORDER_APPLICATION;
      } else if (role === Role.ADMIN_AGENT) {
        postLoginDestination = PHOTO_ORDER_APPLICATION;
      } else if (role === Role.ADMIN_OPS) {
        postLoginDestination = AppRoutes.PaymentVouchers;
      } else if (role === Role.CONTENT_MANAGER) {
        // Dashboard / Users are disabled for Content Manager — land on News.
        postLoginDestination = AppRoutes.NewsManagement;
      }
      sessionStorage.setItem(POST_LOGIN_DESTINATION_KEY, postLoginDestination);
      sessionStorage.setItem(POST_LOGIN_WELCOME_PENDING_KEY, 'true');
      history.push(AppRoutes.Home);

      toastSuccess('Login verified successfully');
      setLoading(false);
    } else {
      toastError(
        response?.data
          ? getLoginErrorMessage(response.data.message)
          : 'Could not reach the server. Please check your connection and try again.'
      );
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="auth-page auth-page--standalone">
        <div className="auth-page__card">
          <div className="auth-page__header">
            <h2 className="auth-page__title">Welcome back</h2>
            <p className="auth-page__subtitle">
              Sign in to access the Documitra management console.
            </p>
          </div>

          <Form
            name="normal_login"
            className="auth-form"
            layout="vertical"
            initialValues={{ remember: true }}
            onFinish={onFinish}
          >
            <Form.Item
              name="email"
              label="Email address"
              className="auth-form__item"
              rules={[
                {
                  required: true,
                  message: 'Please input your Email Address!',
                },
              ]}
            >
              <Input
                className="auth-form__input"
                style={{ borderRadius: '8px' }}
                placeholder="you@company.com"
                prefix={<Mail size={18} strokeWidth={2} className="auth-form__icon" />}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              className="auth-form__item"
              rules={[
                {
                  required: true,
                  message: 'Please enter the valid Password here!',
                },
              ]}
            >
              <Input.Password
                className="auth-form__input"
                style={{ borderRadius: '8px' }}
                placeholder="Enter your password"
                prefix={<Lock size={18} strokeWidth={2} className="auth-form__icon" />}
                size="large"
              />
            </Form.Item>

            <div className="auth-form__forgot-row">
              <Link className="auth-form__forgot-link" to={`${AppRoutes.ForgotPassword}`}>
                Forgot password?
              </Link>
            </div>

            <div className="auth-form__submit-row">
              <Form.Item style={{ marginBottom: 0 }}>
                <InvertFillButton
                  variant="blue"
                  size="lg"
                  fontSize="lg"
                  fontWeight="semibold"
                  className="auth-form__submit-btn"
                  type="submit"
                  disabled={loading}
                >
                  <span>{loading ? 'Signing in…' : 'Sign in'}</span>
                </InvertFillButton>
              </Form.Item>
            </div>
          </Form>

          <div className="auth-page__footer">
            <span className="auth-page__footer-muted">
              Protected by your organization&apos;s security policies.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
